import type { Scene } from '../types'

export const architectureScenes: Scene[] = [
  {
    id: 'ARCH-01',
    chapter: 'architecture',
    route: '/architecture/overview',
    title: 'Architecture at a Glance',
    headline: 'Six architectural elements, each carrying part of the delivery.',
    supporting:
      'Governed objects, an event layer, workflow, AI by design, security by design and native GIS are platform capabilities — built once and inherited by every LAMS module.',
    visual: 'architecture-glance',
    whyItMatters:
      'Because these capabilities belong to the platform rather than to individual screens, every module the District accepts — acquisition, management, disposition — inherits the same security, automation, GIS and AI behavior. Quality is built once and reused, not re-implemented in every sprint.',
    sourceRefs: [
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Sections 2-5, 12-13, 16-19' },
      { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Sections 4.8, 6.1-6.3' },
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Evaluation and Award — define any use of AI' },
    ],
    presenterNotes: [
      'One sentence per element, always in the same shape: what it is, and what it guarantees for delivery.',
      'Objects: new needs are configured on the model, not rebuilt screen by screen. Event layer: nothing an integration or automation does is silently lost. Workflow: LAS, Committee and Board approvals run as governed flows. Security: redaction holds on every path. GIS: maps are part of the work.',
      'AI by design carries the disclosure the RFP requires. In the product: an assistive AI Information Assistant on local Gemma inference, under the same access control, making no decisions. In delivery: AI tools may assist engineering — code analysis, tests, documentation — with human engineers responsible. The proposal itself was prepared with AI-assisted tools and reviewed and approved by the team.',
    ],
    estimatedSeconds: 90,
    rfpAlignment: ['Evaluation and Award — define any use of AI in the software solution', 'Section 5.E — Security Standards', 'Section 5.E — GIS Centrality'],
    evalTags: ['EVAL-WP', 'EVAL-OUTCOME'],
    exploreDetails: [
      {
        title: 'The six elements',
        table: {
          head: ['Element', 'What it is', 'Why it matters for delivering LAMS'],
          rows: [
            ['Objects', 'Business meaning modelled as governed platform objects — persistent, conceptual, integration-backed or computed — with composable capabilities such as Auditable, Stateful, GeoEnabled, DocumentEnabled, ApprovalEnabled and AIEnabled.', 'A capability added to an object arrives with its security, events and history already governed, so the platform evolves without rebuilding the application.'],
            ['Event layer', 'A business change and its domain event written in the same SQL Server transaction; workers claim jobs by lease, with retries, idempotency and correlation.', 'Integrations and automations are queued, retried and traceable. If an external system is unavailable, the work is pending and visible — not lost.'],
            ['Workflow', 'State machines with transitions, guards, required data, permissions and approvals, orchestrated by execution pipelines.', 'Acquisition and disposition approvals, due diligence and critical-path tracking run as governed, auditable flows with tasks and deadlines.'],
            ['AI by design', 'Governed semantic projections of business objects, permission-aware retrieval and locally hosted Gemma inference via Ollama.', 'Assistive answers from District information that stays in District infrastructure, with no metered external LLM API and no autonomous decisions.'],
            ['Security by design', 'Entra ID single sign-on, role-based access control, row-level security and field policy, enforced in backend services.', 'Rare-species locations, archaeological sites and negotiation data stay restricted across screens, reports, exports, GIS views and AI retrieval.'],
            ['GIS', 'GeoEnabled objects with SQL Server spatial persistence where LAMS owns geometry, and ArcGIS as the District GIS provider.', 'Triage, planning and evaluation happen with geographic context inside the workflow, while the District keeps maintaining parcel geometry in ArcGIS.'],
          ],
        },
        sources: [
          { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Sections 2-5, 12-19' },
          { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Sections 6.1-6.3' },
        ],
      },
      {
        title: 'AI use disclosure',
        table: {
          head: ['Context', 'Use', 'Governance'],
          rows: [
            ['Proposal preparation', 'AI-assisted tools supported research, analysis, drafting, consistency review and technical structuring of the proposal.', 'The proposing team reviewed and approved the final content and remains responsible for every assumption, representation and commitment.'],
            ['Software engineering', 'AI may assist development, code analysis, test generation, technical documentation and other engineering-productivity activities.', 'Human engineers remain responsible for architecture, implementation decisions, code review, security, testing and release quality.'],
            ['LAMS solution', 'The AI Information Assistant lets authorized users retrieve, summarize and consolidate LAMS information in natural language, on Google Gemma served locally through Ollama.', 'Role-based access, traceability, data governance and human oversight. No metered external LLM API for normal inference; the assistant does not approve District decisions.'],
            ['Continuous improvement', 'AI may assist analysis of recurring issues, documentation, test coverage, backlog refinement and optimization opportunities.', 'Any resulting change remains subject to normal governance, validation, security review, testing and release control.'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Section 4.8' }],
      },
    ],
  },

  {
    id: 'ARCH-02',
    chapter: 'architecture',
    route: '/architecture/objects',
    title: 'Metadata-Driven Objects',
    headline: 'Business meaning comes before database structure.',
    supporting:
      'A platform object is a logical business capability, not necessarily a database table. It may be persistent, conceptual, integration-backed or computed.',
    visual: 'platform-object',
    callouts: [
      { label: 'Auditable' },
      { label: 'Stateful' },
      { label: 'GeoEnabled' },
      { label: 'DocumentEnabled' },
      { label: 'ApprovalEnabled' },
      { label: 'AIEnabled' },
    ],
    whyItMatters:
      'LAMS can evolve without repeatedly rebuilding the application architecture. A capability added to an object — audit, geography, approvals, AI — arrives with its security, events and history already governed.',
    sourceRefs: [
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Section 2' },
    ],
    presenterNotes: [
      'The key idea: capabilities are composed onto objects, not re-implemented per screen.',
      'Use PERCH as the example of an integration-backed object — it has no API, so LAMS reads District-provided SQL views, but it still appears as a governed object to the rest of the platform.',
    ],
    estimatedSeconds: 60,
    evalTags: ['EVAL-WP'],
  },

  {
    id: 'ARCH-03',
    chapter: 'architecture',
    route: '/architecture/technical',
    title: 'Technical Architecture',
    headline: 'Modern application architecture, aligned to the District environment.',
    supporting:
      'React and TypeScript over a Node.js and TypeScript service layer, on the District\'s own on-premises Microsoft SQL Server, deployed as containers on District-provided infrastructure.',
    visual: 'technical-stack',
    callouts: [
      { label: 'District-hosted SQL Server', detail: 'The RFP\'s highly preferred option, and our baseline' },
      { label: 'Entra ID SSO', detail: 'Existing Microsoft 365 Enterprise E3 environment' },
      { label: 'Docker deployment', detail: 'Consistent promotion across environments' },
      { label: 'Local AI inference', detail: 'Gemma and Ollama inside District infrastructure' },
      { label: '~20 users, ~10 concurrent', detail: 'Scalable beyond the baseline' },
    ],
    whyItMatters:
      'The District said it would provide and maintain the server infrastructure, and that on-premises SQL Server is highly preferred — adding that scoring will reflect that. We proposed the option the District preferred rather than the option that would be easier to host.',
    sourceRefs: [
      { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Section 6, pp.16-18' },
      { kind: 'qa', label: 'Source: SRWMD Questions & Answers', locator: 'Q6-Q9, p.2' },
    ],
    presenterNotes: [
      'Lead with the hosting decision, because the District told us scoring would reflect it.',
      'The AI line is a differentiator: inference runs locally on Gemma via Ollama, so normal AI requests never transmit District operational data to an external hosted LLM service, and there is no metered external API cost.',
      'Sizing is honest: roughly 20 users with 10 concurrent, scalable. Do not inflate this.',
      'If asked about infrastructure specifics: Windows Server 2025 on VMware, with final container host and AI compute sizing confirmed during Sprint 0.',
    ],
    estimatedSeconds: 100,
    rfpAlignment: [
      'Section 5.A — Data Storage, on-premises SQL Server highly preferred',
      'Section 5.A — User Authentication and Authorization, Entra ID SSO preferred',
      'Section 5.E — Collaborative Use',
      'Section 5.E — Technical Support',
    ],
    evalTags: ['EVAL-WP', 'EVAL-TC'],
    exploreDetails: [
      {
        title: 'Proposed stack',
        table: {
          head: ['Layer', 'Technology', 'Architectural role'],
          rows: [
            ['Frontend', 'React + TypeScript', 'Responsive browser-based experience for operational workflows, dashboards, GIS context, reporting and administration.'],
            ['Backend / API', 'Node.js + TypeScript', 'Central application and business-service layer: workflows, authorization, business rules, integrations, document generation and application APIs.'],
            ['Database', 'Microsoft SQL Server', 'Primary operational data store for business records, relationships, workflow state and historical information.'],
            ['Identity & access', 'Microsoft Entra ID', 'Single Sign-On using the District\'s existing Microsoft environment, complemented by application-level role-based authorization.'],
            ['GIS', 'ESRI ArcGIS APIs / SDK', 'Integration of District ArcGIS information into acquisition, management, disposition, planning and operational workflows.'],
            ['Enterprise integration', 'API, SQL views, scheduled exchange', 'Integration with CivicPlus, PaperVision, AccuFund, PERCH and other approved District systems.'],
            ['AI capability', 'Google Gemma + Ollama', 'Locally hosted language-model inference within approved LAMS infrastructure, without a metered external LLM API for normal inference.'],
            ['Hosting', 'District-provided on-premises infrastructure', 'District-hosted SQL Server environment preferred by the RFP; final host sizing confirmed during Sprint 0.'],
            ['Deployment', 'Docker / containerized', 'Consistent packaging and controlled release promotion between environments.'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Section 6.1' }],
      },
      {
        title: 'Integration approach by system',
        table: {
          head: ['System', 'Integration approach', 'Treatment'],
          rows: [
            ['ArcGIS Server / ArcGIS Online', 'ESRI APIs / SDK and available GIS services', 'Provide GIS visualization, geographic context, triage and planning support inside LAMS while preserving the District ArcGIS environment.'],
            ['CivicPlus', 'Available API', 'Receive and associate application and form-submission information with LAMS acquisition records.'],
            ['PaperVision Enterprise', 'Available API', 'Search, reference and link documents while preserving PaperVision as the document repository.'],
            ['AccuFund', 'Scheduled CSV or other supported mechanism', 'Import applicable financial information into the LAMS operational context.'],
            ['PERCH', 'District-provided SQL Server views', 'Read-only access to applicable PERCH information and association with LAMS records.'],
            ['Microsoft Entra ID / Microsoft 365', 'Microsoft identity and supported service interfaces', 'Authentication, identity integration and applicable Microsoft ecosystem capabilities.'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Section 6.3' }],
      },
      {
        title: 'Environment and infrastructure baseline',
        bullets: [
          'Separated environments: Development / Integration → Staging / UAT → Production.',
          'The proposer provides the UAT environment, as the District clarified in the Questions & Answers.',
          'Current District infrastructure baseline: Windows Server 2025 on VMware virtual machines.',
          'The District indicated no more than four VPN licences will be available for secure remote implementation access.',
          'No specialized end-user hardware or dedicated desktop application is required beyond a supported browser.',
          'Final container host sizing, storage, backup coordination, VPN procedures and AI compute requirements are confirmed during Sprint 0.',
        ],
        sources: [
          { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Section 6.5' },
          { kind: 'qa', label: 'SRWMD Questions & Answers', locator: 'Q27' },
        ],
      },
    ],
  },

  {
    id: 'ARCH-04',
    chapter: 'architecture',
    route: '/architecture/integration',
    title: 'Integration View',
    headline: 'LAMS connects the District landscape without replacing its systems of record.',
    supporting:
      'Each external system is reached through its own adapter, using the mechanism that system supports. Exchanges run through the event layer, so failures are monitored, traceable and reprocessable.',
    visual: 'integration-map',
    whyItMatters:
      'The District keeps the systems that already work. A change to an external API, file format or database view is absorbed by one adapter rather than by LAMS business logic — and a failed exchange becomes a visible, recoverable item rather than a silent gap in the land record.',
    sourceRefs: [
      { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Sections 3.5, 6.3' },
      { kind: 'qa', label: 'Source: SRWMD Questions & Answers', locator: 'Q5, pp.1-2' },
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Section 3' },
    ],
    presenterNotes: [
      'Go around the map once, naming the mechanism for each system: ArcGIS through the ESRI APIs and SDK; CivicPlus and PaperVision through their APIs; AccuFund by scheduled CSV or another supported mechanism confirmed in discovery; PERCH through read-only SQL Server views the District provides; Entra ID for single sign-on.',
      'Two boundaries are worth saying out loud. PaperVision stays the document repository — LAMS links, it does not migrate documents. And LAMS does not edit parcel geometry — the District keeps maintaining it in ArcGIS.',
      'Field Maps / Collector and Survey123 stay in place; their applicable data is integrated, not replaced.',
    ],
    estimatedSeconds: 75,
    rfpAlignment: ['Section 5.A — Integration', 'Section 5.E — GIS Centrality'],
    evalTags: ['EVAL-WP'],
    exploreDetails: [
      {
        title: 'Integration by system',
        table: {
          head: ['System', 'Mechanism', 'Direction and treatment', 'Backlog'],
          rows: [
            ['ArcGIS Server 11.1 / ArcGIS Online', 'ESRI APIs / SDK and available GIS services', 'GIS visualization, triage maps, planning context and spatial analysis inside LAMS, preserving the District ArcGIS environment. No direct parcel-geometry editing by LAMS.', 'GI-01, GI-02'],
            ['CivicPlus', 'Available API', 'Receives applicable application and form-submission information and associates it with acquisition records.', 'GI'],
            ['PaperVision Enterprise', 'Available API', 'Search, reference and link documents while PaperVision remains the document repository.', 'GI'],
            ['AccuFund', 'Scheduled CSV or other supported mechanism confirmed during discovery', 'Imports applicable financial information into the operational context.', 'GI'],
            ['PERCH', 'District-provided SQL Server views', 'Read-only access and association of relevant records with LAMS data.', 'GI'],
            ['Microsoft Entra ID / Microsoft 365', 'Microsoft identity and supported service interfaces', 'Single sign-on, identity integration and applicable Microsoft ecosystem capabilities.', 'CP'],
            ['ArcGIS Field Maps / Collector and Survey123', 'Existing District field applications', 'Remain authoritative for field workflows; applicable field-collected data is integrated into land-management context.', 'GI'],
          ],
        },
        sources: [
          { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Section 6.3' },
          { kind: 'qa', label: 'SRWMD Questions & Answers', locator: 'Q5' },
        ],
      },
      {
        title: 'How integration behaves',
        bullets: [
          'An integration layer isolates external-system dependencies from the core business logic; adapters separate interface details from LAMS business functions.',
          'Integration execution includes monitoring and traceable error handling, so failed or incomplete exchanges can be identified, investigated, corrected and, where appropriate, reprocessed.',
          'Asynchronous exchanges run as durable jobs in SQL Server with leases, retries, idempotency and correlation identifiers.',
          'Interfaces, credentials and access are validated during Sprint 0, with interface-specific test plans and early connectivity tests.',
          'Required integrations are validated end-to-end at milestone M6, before the UAT release candidate.',
        ],
        sources: [
          { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Sections 4.7, 5.3, 6.3' },
          { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Section 3' },
        ],
      },
    ],
  },

  {
    id: 'ARCH-05',
    chapter: 'architecture',
    route: '/architecture/event-driven',
    title: 'Event-Driven Runtime',
    headline: 'Automation should be durable, traceable and recoverable.',
    supporting:
      'A business change and its domain event are written in the same SQL Server transaction. Workers then claim work by lease, with retries, idempotency and correlation.',
    visual: 'event-pipeline',
    whyItMatters:
      'Integrations and automations do not depend on fragile synchronous chains. If CivicPlus is unreachable when an application arrives, the work is queued and retried — it is not lost, and someone can see that it is pending.',
    sourceRefs: [
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Section 3' },
      { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Section 6.3 — monitoring and traceable error handling' },
    ],
    presenterNotes: [
      'The proposal already commits to monitoring and traceable error handling so failed or incomplete exchanges can be identified, investigated, corrected and reprocessed. This architecture is how we deliver that.',
      'SQL Server stays the durable source of truth for business data, events, jobs, schedules and audit. No separate broker is required for the initial implementation.',
      'Keep this brief — it is a reassurance scene, not a deep dive.',
    ],
    estimatedSeconds: 60,
    rfpAlignment: ['Section 5.A — Integration'],
    evalTags: ['EVAL-WP', 'EVAL-OUTCOME'],
  },

  {
    id: 'ARCH-06',
    chapter: 'architecture',
    route: '/architecture/security',
    title: 'Security by Design',
    headline: 'The same authorization model follows every access path.',
    supporting:
      'Entra ID Single Sign-On for authentication, role-based access control for authorization, and role-based redaction for statutorily restricted information — enforced in backend services, not in the interface.',
    visual: 'security-chain',
    callouts: [
      { label: 'Rare-species locations' },
      { label: 'Archaeological-site information' },
      { label: 'Acquisition-negotiation data' },
    ],
    whyItMatters:
      'Restricted information stays restricted on every path it could leave by — screens, reports, exports, GIS views and AI-assisted retrieval. Hiding a field in the interface is not enforcement.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 5.E — Security Standards, role-based security' },
      { kind: 'qa', label: 'Source: SRWMD Questions & Answers', locator: 'Q93, p.11 — RBAC is the only requirement' },
      { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Section 6.2' },
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Section 5' },
    ],
    presenterNotes: [
      'Start with what was committed: Entra ID SSO, RBAC, and redaction of statutorily restricted data. The District confirmed RBAC is the only framework requirement — no NIST 800-53 or CJIS obligation applies here.',
      'The redaction commitment is the one to emphasize. Rare-species locations and archaeological sites are exempt from disclosure, and the District carries that obligation on every report it produces.',
      'Then show the fuller authorization chain as the architecture we designed to deliver it. Be clear that the additional layers are our engineering, not extra promises.',
    ],
    estimatedSeconds: 75,
    rfpAlignment: [
      'Section 5.A — User Authentication and Authorization',
      'Section 5.E — Security Standards, role-based security',
    ],
    evalTags: ['EVAL-WP'],
    exploreDetails: [
      {
        title: 'Committed in the proposal',
        bullets: [
          'Authentication through Microsoft Entra ID Single Sign-On, using the District\'s existing identity environment.',
          'Role-Based Access Control determining which modules, records, functions and actions are available to each user.',
          'Role-based redaction and exemption tagging for rare-species locations, archaeological-site information and applicable acquisition-negotiation data.',
          'Enforcement of those controls across application screens, reports, exports, GIS-related views and AI-assisted information retrieval.',
          'Permissions enforced in backend services, not only in the user interface.',
          'Least-privilege access, encrypted communication, controlled credential and secret management, activity traceability, environment separation and controlled administrative privileges.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Section 6.2' }],
      },
      {
        title: 'Proposed authorization chain',
        body: [
          'The Studio architecture implements the committed model as an explicit chain. Execution never bypasses object authorization, row-level security, field policy or state and business guards.',
        ],
        table: {
          head: ['Boundary', 'Question it answers'],
          rows: [
            ['Application access', 'May this group enter and use this application?'],
            ['Application Action', 'May it initiate this business capability?'],
            ['Object Action', 'May it execute this operation against this object type?'],
            ['Row-level security', 'May it access this specific record?'],
            ['Field policy and guards', 'Which attributes may be read or changed, and are business and state conditions satisfied?'],
          ],
        },
        sources: [
          { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Section 5' },
        ],
      },
      {
        title: 'What the District required',
        body: [
          'A proposer asked whether a specific security framework was required — NIST 800-53, CJIS or a StateRAMP equivalent — or whether standard RBAC was sufficient. The District answered: "RBAC is the only requirement."',
          'Row-level security and field policy are architectural choices Merlion made to deliver RBAC and redaction reliably. They exceed the stated requirement and are presented as design, not as additional contractual commitments.',
        ],
        sources: [{ kind: 'qa', label: 'SRWMD Questions & Answers', locator: 'Q93, p.11' }],
      },
    ],
  },

  {
    id: 'ARCH-07',
    chapter: 'architecture',
    route: '/architecture/gis',
    title: 'Native GIS Architecture',
    headline: 'LAMS owns the business model. ArcGIS remains the District GIS ecosystem.',
    supporting:
      'GeoEnabled is a composable object capability. SQL Server provides spatial persistence where LAMS owns the geometry; ArcGIS provides maps, layers, services and the District\'s GIS assets.',
    visual: 'gis-architecture',
    whyItMatters:
      'Geography stays part of the business context instead of becoming a separate disconnected map — and the business domain never becomes synonymous with an ArcGIS layer or a physical spatial table.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 5.E — GIS Centrality' },
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Sections 12-15' },
    ],
    presenterNotes: [
      'The provider abstraction is the insurance policy: if a future workload needs PostGIS or another spatial engine, the provider changes without redefining LAMS objects, actions, workflows or UI.',
      'PostGIS is explicitly not required for the initial implementation. Do not imply we are proposing it.',
      'Repeat the boundary: the District maintains parcel geometry. We consume it.',
    ],
    estimatedSeconds: 90,
    rfpAlignment: ['Section 5.E — GIS Centrality'],
    evalTags: ['EVAL-WP'],
    exploreDetails: [
      {
        title: 'GIS persistence and provider strategy',
        table: {
          head: ['Layer', 'Initial SRWMD choice', 'Architectural position'],
          rows: [
            ['Business persistence', 'SQL Server', 'System of record for LAMS objects, metadata, security, events and jobs.'],
            ['Spatial persistence', 'SQL Server geometry/geography where LAMS owns the spatial attribute', 'Part of the object persistence model, abstracted by the GIS engine.'],
            ['GIS ecosystem', 'ArcGIS', 'Maps, layers, services and integration with existing District GIS assets.'],
            ['Alternative spatial engine', 'Not required initially', 'PostGIS or another provider remains an extension option, not a core dependency.'],
          ],
        },
        sources: [
          { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Section 14' },
        ],
      },
      {
        title: 'GeoEnabled capability contract',
        table: {
          head: ['Metadata', 'Purpose'],
          rows: [
            ['geometryField / geometryType', 'Identifies the spatial attribute and the allowed Point, Line or Polygon family.'],
            ['spatialReference', 'Defines the coordinate reference expected by the object and provider binding.'],
            ['layerBinding', 'Maps the platform object to an ArcGIS layer or service when required.'],
            ['editable', 'Controls whether geometry may be changed through approved actions and components.'],
            ['spatialRelationships', 'Declares supported contains, within, intersects, touches or proximity relationships.'],
            ['mapPresentation', 'Defines map component defaults such as labels, popup fields and display behaviour.'],
          ],
        },
        sources: [
          { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Section 13' },
        ],
      },
    ],
  },

  {
    id: 'ARCH-08',
    chapter: 'architecture',
    route: '/architecture/ai',
    title: 'AI by Design',
    headline: 'AI understands governed business objects — not raw database tables.',
    supporting:
      'Inference runs locally on District infrastructure. Vector search returns candidates only; the policy resolver decides what the requesting user is actually allowed to see.',
    visual: 'ai-pipeline',
    callouts: [
      { label: 'Runs locally', detail: 'Gemma via Ollama, inside approved LAMS infrastructure' },
      { label: 'No metered external API', detail: 'Normal inference needs no outbound LLM service' },
      { label: 'Same access control', detail: 'AI cannot retrieve what the user could not retrieve' },
      { label: 'Assistive only', detail: 'It does not approve or execute District land decisions' },
    ],
    whyItMatters:
      'AI receives governed, security-aware business meaning instead of uncontrolled database access — and District operational data does not leave District infrastructure to get an answer.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Evaluation and Award — define any use of AI' },
      { kind: 'qa', label: 'Source: SRWMD Questions & Answers', locator: 'Q29, p.4 — high level, for transparency' },
      { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Sections 6.1, 6.2, 9.3' },
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Sections 16-19' },
    ],
    presenterNotes: [
      'The RFP requires proposers to define any use of AI. The District clarified it wants this at a high level, for transparency. Be direct and complete rather than promotional.',
      'The local-inference commitment is the headline for a government buyer: no District operational data is transmitted to an external hosted LLM service for normal inference, and there is no metered API charge.',
      'Say clearly that AI is assistive and governed. It does not autonomously approve or execute District land-management decisions, and it cannot see anything the requesting user could not see.',
      'Avoid the word "agent". This is retrieval and assistance over governed context.',
    ],
    estimatedSeconds: 100,
    rfpAlignment: ['Evaluation and Award — define any use of AI in the software solution'],
    evalTags: ['EVAL-WP', 'EVAL-OUTCOME'],
    exploreDetails: [
      {
        title: 'How governed AI context is produced',
        table: {
          head: ['Stage', 'What happens'],
          rows: [
            ['AIEnabled capability', 'An object declares that it can expose governed semantic representations to platform AI services.'],
            ['AI Semantic Profile', 'Versioned governance metadata defining what semantic content is produced, and how it is chunked, indexed, filtered, secured and refreshed.'],
            ['Semantic Projection', 'A human-readable, deterministic representation assembled from approved fields and relationships — not a raw row dump.'],
            ['Chunk builder and embedding', 'The runtime builds chunks and embeddings. Embeddings are derived indexes; SQL Server remains the business source of truth.'],
            ['Retrieval', 'Structured filters plus vector search return object candidates only.'],
            ['Policy resolver', 'Row-level security and field policy authorize what may actually be hydrated for this user.'],
            ['Authorized AI context', 'The model receives only governed, permitted content.'],
          ],
        },
        sources: [
          { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Sections 16-19' },
        ],
      },
      {
        title: 'AI use disclosure',
        bullets: [
          'In the software solution: an AI Information Assistant for retrieval and operational assistance over governed LAMS objects, running on locally hosted Gemma inference via Ollama.',
          'The assistant operates under the same access-control model as the rest of LAMS and will not surface information the requesting user is unauthorized to retrieve.',
          'The assistant does not autonomously approve or execute District land-management decisions. Human oversight is retained.',
          'No metered external LLM API consumption is required for normal AI inference, and no separate recurring AI API charge is billed to the District under the baseline implementation.',
          'If the final Gemma model sizing requires additional acceleration capacity, that requirement is identified during Sprint 0 for District approval rather than assumed.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Sections 6.1, 6.2, 9.3' }],
      },
    ],
  },

  {
    id: 'ARCH-09',
    chapter: 'architecture',
    route: '/architecture/evolution',
    title: 'Controlled Evolution',
    headline: 'Configuration is versioned, validated and promoted.',
    supporting:
      'Application metadata is treated as a versioned package that moves Draft → Validate → Publish, and then through Development, Staging/UAT and Production.',
    visual: 'application-package',
    callouts: [
      { label: 'Domain' },
      { label: 'Experience' },
      { label: 'Execution' },
      { label: 'Governance' },
      { label: 'Version' },
    ],
    whyItMatters:
      'Change after go-live stays governed rather than becoming uncontrolled customization. The District gets rollback, version history and a validation gate — the same discipline applied to configuration that is normally applied only to code.',
    sourceRefs: [
      { kind: 'architecture', label: 'Merlion Proposed Architecture', locator: 'Studio Architecture v0.5, Section 9' },
      { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Section 6.5 — environment promotion' },
    ],
    presenterNotes: [
      'This is the answer to "what happens in year three?" — a fair question for a system expected to last.',
      'Publishing validation checks field and relationship existence, capability compatibility, security classification and trigger validity before anything reaches an environment.',
    ],
    estimatedSeconds: 60,
    evalTags: ['EVAL-WP', 'EVAL-OUTCOME'],
  },
]
